import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, ChevronLeft, ChevronRight, Monitor, Smartphone, Tablet, Globe, Share2, Link, X, Loader2 } from 'lucide-react';
import { getSessions } from '../api/client';
import api from '../api/client';
import SessionReplay from '../components/sessions/SessionReplay';
import { formatDateTime, formatDurationMs } from '../utils/formatters';
import clsx from 'clsx';
import { useAuth } from '../hooks/useAuth';

export default function ReplayPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPage = searchParams.get('page') || '1';
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [shareModal, setShareModal] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const shareMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/sessions/${sessionId}/share`);
      return res.data.shareToken as string;
    },
    onSuccess: (token) => setShareToken(token),
  });

  const revokeMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/sessions/${sessionId}/share`);
    },
    onSuccess: () => setShareToken(null),
  });

  function copyLink() {
    if (!shareToken) return;
    const url = `${window.location.origin}/share/${shareToken}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  // Fetch session list to enable prev/next navigation
  const { data: sessionData } = useQuery({
    queryKey: ['sessions-for-nav', returnPage],
    queryFn: () => getSessions({ page: parseInt(returnPage, 10), pageSize: 25 }),
  });

  const sessionList = sessionData?.data || [];
  const currentIndex = sessionList.findIndex((s: any) => s.id === sessionId);
  const prevSession = currentIndex > 0 ? sessionList[currentIndex - 1] : null;
  const nextSession = currentIndex >= 0 && currentIndex < sessionList.length - 1 ? sessionList[currentIndex + 1] : null;

  // Get current session metadata from the list
  const currentSession = sessionList.find((s: any) => s.id === sessionId) as any;

  if (!sessionId) {
    return <div>Session not found</div>;
  }

  function navigateToSession(id: string) {
    // Mark as watched
    const watched = JSON.parse(localStorage.getItem('vantage_watched_sessions') || '[]');
    if (!watched.includes(id)) {
      watched.push(id);
      if (watched.length > 500) watched.splice(0, watched.length - 500);
      localStorage.setItem('vantage_watched_sessions', JSON.stringify(watched));
    }
    navigate(`/sessions/${id}?page=${returnPage}`);
  }

  function getDeviceIcon(device: string) {
    switch ((device || '').toLowerCase()) {
      case 'mobile': return <Smartphone className="h-4 w-4" />;
      case 'tablet': return <Tablet className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  }

  // Parse source URL for display
  const entryUrl = currentSession?.entryUrl || currentSession?.entryPage || '';
  let sourceDisplay = '';
  let utmSource = '';
  let utmCampaign = '';
  let utmMedium = '';
  try {
    const u = new URL(entryUrl);
    sourceDisplay = u.pathname + (u.search ? u.search.substring(0, 60) + '...' : '');
    utmSource = u.searchParams.get('utm_source') || '';
    utmCampaign = u.searchParams.get('utm_campaign') || '';
    utmMedium = u.searchParams.get('utm_medium') || '';
  } catch {
    sourceDisplay = entryUrl;
  }

  const device = currentSession?.deviceType || currentSession?.device || '';
  const browser = currentSession?.browser || '';
  const browserVersion = currentSession?.browserVersion || '';
  const os = currentSession?.os || '';
  const durationMs = currentSession?.durationMs ?? 0;
  const pagesVisited = currentSession?.pagesVisited ?? currentSession?.pageCount ?? 0;

  return (
    <div className="space-y-3">
      {/* Top Bar: Back + Navigation + Session Info */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(`/sessions?page=${returnPage}`)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Sessions
        </button>

        {/* Prev/Next Navigation + Share */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => prevSession && navigateToSession(prevSession.id)}
            disabled={!prevSession}
            className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Previous
          </button>
          {currentIndex >= 0 && (
            <span className="text-xs text-gray-400">
              {currentIndex + 1} of {sessionList.length}
            </span>
          )}
          <button
            onClick={() => nextSession && navigateToSession(nextSession.id)}
            disabled={!nextSession}
            className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </button>

          {isAdmin && (
            <button
              onClick={() => setShareModal(true)}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
            >
              <Share2 className="h-3.5 w-3.5" />
              Share
            </button>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {shareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Share Session Recording</h2>
              <button onClick={() => setShareModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Generate a public link so others can watch this session recording without logging in.
              The link stays active until you revoke it.
            </p>

            {!shareToken ? (
              <button
                onClick={() => shareMutation.mutate()}
                disabled={shareMutation.isPending}
                className="btn-primary w-full"
              >
                {shareMutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</>
                ) : (
                  <><Share2 className="mr-2 h-4 w-4" />Generate Share Link</>
                )}
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <Link className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="flex-1 truncate font-mono text-xs text-gray-600">
                    {window.location.origin}/share/{shareToken}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyLink}
                    className={clsx('btn-primary flex-1', copied && 'bg-green-600 hover:bg-green-700')}
                  >
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Revoke this share link? Anyone with the link will lose access.')) {
                        revokeMutation.mutate();
                      }
                    }}
                    disabled={revokeMutation.isPending}
                    className="btn-secondary text-red-600 hover:bg-red-50"
                  >
                    Revoke
                  </button>
                </div>
                <p className="text-xs text-gray-400">
                  Only this session is public. All other sessions remain private.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Session Metadata Bar */}
      {currentSession && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-xs">
          {/* Device & Browser */}
          <div className="flex items-center gap-1.5 text-gray-600">
            {getDeviceIcon(device)}
            <span className="font-medium capitalize">{device}</span>
            {browser && (
              <span className="text-gray-400">
                {browser} {browserVersion ? browserVersion.split('.')[0] : ''}
              </span>
            )}
            {os && <span className="text-gray-400">/ {os}</span>}
          </div>

          <span className="text-gray-200">|</span>

          {/* Duration & Pages */}
          <span className="text-gray-500">
            {formatDurationMs(durationMs)} · {pagesVisited} page{pagesVisited !== 1 ? 's' : ''}
          </span>

          <span className="text-gray-200">|</span>

          {/* Date */}
          <span className="text-gray-500">{formatDateTime(currentSession.startedAt)}</span>

          {/* Tags */}
          {(currentSession.hasRageClicks || currentSession.hasErrors) && (
            <>
              <span className="text-gray-200">|</span>
              <div className="flex items-center gap-1.5">
                {currentSession.hasRageClicks && (
                  <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">rage clicks</span>
                )}
                {currentSession.hasErrors && (
                  <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">errors</span>
                )}
              </div>
            </>
          )}

          {/* Source URL */}
          {entryUrl && (
            <>
              <span className="text-gray-200">|</span>
              <div className="flex items-center gap-1 text-gray-400 min-w-0">
                <Globe className="h-3 w-3 shrink-0" />
                {utmSource ? (
                  <span className="truncate">
                    <span className="font-medium text-gray-600">{utmSource}</span>
                    {utmMedium && <span> / {utmMedium}</span>}
                    {utmCampaign && <span> / {utmCampaign}</span>}
                  </span>
                ) : (
                  <span className="truncate" title={entryUrl}>{sourceDisplay}</span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Session Replay */}
      <SessionReplay sessionId={sessionId} autoPlay={true} />
    </div>
  );
}
